/**
 * El alias con el que un alumno se ve en la pantalla del aula.
 *
 * **Este filtro va a fallar en los dos sentidos**, y está asumido: dejará pasar cosas y rechazará
 * algún nombre legítimo. La red de seguridad no es esta lista, es que el profesor pueda expulsar.
 *
 * Lo que sí se puede evitar es un falso positivo tonto. La versión anterior de este diseño
 * marcaba «Fagot» —el instrumento— y «Nigeria» como ofensivos, porque plegaba el texto para cazar
 * disfraces y **el plegado acorta las raíces después de escribirlas**: una raíz de seis letras
 * con una doble se queda en cinco, y a cinco letras se empieza a marcar media lengua.
 */
/** Máximo visible, contado en grafemas: un emoji o una letra con tilde cuentan uno. */
export const MAX_ALIAS_LENGTH = 20;
export const MIN_ALIAS_LENGTH = 2;
/**
 * Tope de una etiqueta de participante, que **no** es el tope de un alias.
 *
 * El alias lo escribe el alumno y veinte caracteres son de sobra. El nombre real viene de la
 * cuenta de Google y no lo elige nadie: recortarlo a veinte convertía «María del Carmen Fernández
 * de la Vega» en un nombre que no es el suyo, y se lo enseñaba a ella misma bajo un «apareces
 * como». Un nombre partido en su propia pantalla es un fallo, no una medida de espacio.
 *
 * Lo que de verdad protegía ese recorte —que una celda del proyector no reviente— se resuelve
 * donde toca, con `truncate` en CSS, que además pone los puntos suspensivos y deja el nombre
 * entero en el DOM para quien use un lector de pantalla.
 *
 * Este tope sigue existiendo por prudencia: hay cuentas con el nombre, los dos apellidos y el
 * centro dentro, y sesenta grafemas son bastante más de lo que cabe en cualquier fila.
 */
export const MAX_LABEL_LENGTH = 60;
/**
 * Cuenta grafemas, no unidades de código.
 *
 * «Añó» son tres caracteres para una persona aunque sean más para la máquina, y un emoji con
 * modificador es uno solo. Recortar por unidades partiría el emoji por la mitad.
 */
function graphemes(text) {
    const segmenter = new Intl.Segmenter(undefined, { granularity: 'grapheme' });
    return [...segmenter.segment(text)].map((s) => s.segment);
}
export function visibleLength(text) {
    return graphemes(text).length;
}
/** Recorta a un número de grafemas sin partir ninguno. */
export function truncateVisible(text, max) {
    const parts = graphemes(text);
    return parts.length <= max ? text : parts.slice(0, max).join('');
}
/**
 * Quita lo invisible y normaliza los espacios.
 *
 * Las marcas de dirección de texto y los caracteres de anchura cero se usan para colar cosas que
 * no se ven, y no tienen ningún uso legítimo en un apodo de aula.
 */
export function sanitizeVisibleText(text) {
    return (text
        .normalize('NFC')
        // Escritos como escapes a propósito: literales serían invisibles también para quien lea el
        // código, que es exactamente el problema que se quiere evitar.
        .replace(/[\u200B-\u200F\u202A-\u202E\u2060-\u2064\uFEFF]/g, '')
        .replace(/\s+/g, ' ')
        .trim());
}
/**
 * Forma canónica para **comparar identidades**: decidir si dos alias son «el mismo».
 *
 * Cierra el juego del homoglifo —la A cirílica que se ve igual que la latina— sin tocar la eñe ni
 * la ce con cedilla, que son letras de verdad en castellano y catalán y cuya pérdida convertiría
 * «Muñoz» y «Munoz» en la misma persona.
 */
export function foldForIdentity(text) {
    return (sanitizeVisibleText(text)
        .toLowerCase()
        .normalize('NFD')
        // Se quitan las tildes, pero se conservan la eñe y la cedilla recomponiendo después.
        .replace(/[̀-̂̄-̨̦-ͯ]/g, '')
        .normalize('NFC'));
}
/**
 * Forma canónica para **buscar palabrotas**: deshace los disfraces habituales.
 *
 * Es más agresiva que la anterior a propósito, y por eso **no** se usa para comparar identidades:
 * colapsa las letras repetidas, y eso acorta las palabras.
 */
function foldForMatching(text) {
    return (foldForIdentity(text)
        .replace(/[0]/g, 'o')
        .replace(/[1|!]/g, 'i')
        .replace(/[3]/g, 'e')
        .replace(/[4@]/g, 'a')
        .replace(/[5$]/g, 's')
        .replace(/[7]/g, 't')
        .replace(/[^\p{L}\p{N}]/gu, '')
        // Colapsa repeticiones: «puuuta» y «puta» son lo mismo. OJO: esto ACORTA las palabras, y es
        // la razón por la que las raíces de abajo se miden **después** de plegar.
        .replace(/(.)\1+/g, '$1'));
}
/**
 * Palabras completas prohibidas, comparadas con el plegado agresivo.
 *
 * Se comparan por igualdad, no por subcadena: son cortas y buscarlas dentro de otra palabra es
 * exactamente lo que produce falsos positivos.
 */
const BANNED_WORDS = [
    'puta',
    'puto',
    'zorra',
    'maricon',
    'marica',
    'mierda',
    'merda',
    'joder',
    'follar',
    'cabron',
    'gilipollas',
    'subnormal',
    'retrasado',
    'fuck',
    'shit',
    'bitch',
    'whore',
    'cunt',
    'rape',
    'nazi',
    'hitler',
];
/**
 * Insultos que **colisionan con palabras legítimas al plegar**, y por eso se comparan sin
 * deshacer las repeticiones.
 *
 * `faggot` plegado es `fagot`, que es exactamente el instrumento musical. `nigger` plegado es
 * `niger`, que es un país. Con el plegado agresivo son **homógrafos**: no hay forma de
 * distinguirlos, y marcar «Fagot» o «Nigeria» como insulto es peor que dejar pasar «niggger».
 *
 * Se acepta el hueco a conciencia: quien quiera esquivarlo repitiendo una letra lo consigue, y
 * para eso está el profesor, que puede expulsar.
 */
const BANNED_WORDS_EXACT = ['nigger', 'faggot', 'nigga'];
/**
 * Raíces que sí se buscan dentro de la palabra, para cazar «megaputa» o «putita».
 *
 * **La regla es seis letras o más DESPUÉS de plegar**, y hay una prueba que lo comprueba sobre la
 * lista ya plegada. Escribirlo sobre la lista sin plegar es lo que dejó pasar «nigger» → «niger»
 * (cinco) y «faggot» → «fagot» (cinco), que marcaban «Nigeria» y «Fagot» como ofensivos.
 */
const BANNED_STEMS = [
    'gilipoyas',
    'gilipolas',
    'hijodeputa',
    'hijaputa',
    'subnormal',
    'malparido',
    'chupapoyas',
];
/** Mide una raíz como se va a comparar: plegada. */
export function foldedLength(stem) {
    return foldForMatching(stem).length;
}
/** Las raíces cumplen su propia regla. Lo comprueba una prueba, no la buena fe. */
export const MIN_STEM_FOLDED_LENGTH = 6;
function hasProfanity(alias) {
    const folded = foldForMatching(alias);
    if (folded === '')
        return false;
    // Los trozos separados por espacio, cada uno plegado. Y el texto entero sin espacios, que es lo
    // que caza el disfraz de separar las letras: «p u t a».
    const tokens = foldForIdentity(alias)
        .split(' ')
        .map((token) => foldForMatching(token))
        .filter((token) => token !== '');
    const candidates = [...tokens, folded];
    // Pasada 1: palabra completa, con el plegado agresivo.
    for (const word of BANNED_WORDS) {
        if (candidates.includes(foldForMatching(word)))
            return true;
    }
    // Pasada 2: las que colisionan al plegar, comparadas sin deshacer repeticiones.
    const softTokens = foldForIdentity(alias)
        .replace(/[^\p{L}\p{N} ]/gu, '')
        .split(' ')
        .filter((token) => token !== '');
    for (const word of BANNED_WORDS_EXACT) {
        if (softTokens.includes(word))
            return true;
    }
    // Pasada 3: raíces largas dentro de la palabra. Solo raíces largas, por lo dicho arriba.
    for (const stem of BANNED_STEMS) {
        const foldedStem = foldForMatching(stem);
        if (foldedStem.length >= MIN_STEM_FOLDED_LENGTH && folded.includes(foldedStem))
            return true;
    }
    return false;
}
/** Lo que parece una dirección web se rechaza: no es un apodo, es publicidad. */
function looksLikeUrl(alias) {
    return /(?:https?:\/\/|www\.|\.(?:com|net|org|es|cat|io|tv|gg)\b)/i.test(alias);
}
/**
 * Comprueba un alias propuesto.
 *
 * Se usa en el servidor para decidir y en la SPA para avisar mientras se escribe. La decisión de
 * verdad es siempre la del servidor: la del navegador es cortesía.
 */
export function checkAlias(raw) {
    const alias = sanitizeVisibleText(raw);
    if (alias === '')
        return { ok: false, reason: 'empty' };
    const length = visibleLength(alias);
    if (length < MIN_ALIAS_LENGTH)
        return { ok: false, reason: 'tooShort' };
    if (length > MAX_ALIAS_LENGTH)
        return { ok: false, reason: 'tooLong' };
    if (looksLikeUrl(alias))
        return { ok: false, reason: 'looksLikeUrl' };
    if (hasProfanity(alias))
        return { ok: false, reason: 'profanity' };
    return { ok: true, alias };
}
//# sourceMappingURL=alias.js.map